import { useCallback } from "react";

import { getLocalizedString } from "common/components/localization";
import { assertNever } from "common/Support";
import type { RuntimeTheme } from "webapp/lib/theme/theme";
import type { ScanPrecision, Tolerance} from "./quagga-bindings";
import { precisionItems, toleranceItems } from "./quagga-bindings";
import { SettingsWrapper } from "./code-scanner-style";
import { X } from "common/react-plucked";
import InlinePivotView from "webapp/ui/views/inline-pivot/inline-pivot-view";
import type { AppKind } from "common/components/app-kind";

type SettingsProps = Readonly<{
    appKind: AppKind;
    orientation: "portrait" | "landscape";
    theme: RuntimeTheme;
    currentPrecision: ScanPrecision;
    currentTolerance: Tolerance;
    onClose: () => void;
    onPrecisionChange: (p: ScanPrecision) => void;
    onToleranceChange: (a: Tolerance) => void;
}>;

export function SettingsOverlay({
    orientation,
    theme,
    currentPrecision,
    currentTolerance,
    onPrecisionChange,
    onToleranceChange,
    onClose,
    appKind,
}: SettingsProps): JSX.Element {
    function labelForPrecision(p: ScanPrecision): string {
        switch (p) {
            case "coarse":
                return getLocalizedString("coarsePrecision", appKind);
            case "normal":
                return getLocalizedString("normalPrecision", appKind);
            case "fine":
                return getLocalizedString("finePrecision", appKind);
            default:
                return assertNever(p);
        }
    }

    function labelForTolerance(t: Tolerance): string {
        switch (t) {
            case "loose":
                return getLocalizedString("looseTolerance", appKind);
            case "normal":
                return getLocalizedString("normalTolerance", appKind);
            case "tight":
                return getLocalizedString("tightTolerance", appKind);
            default:
                return assertNever(t);
        }
    }

    const onPrecisionPick = useCallback(
        (index: number) => {
            if (index >= precisionItems.length) {
                onPrecisionChange("fine");
            } else {
                onPrecisionChange(precisionItems[index]);
            }
        },
        [onPrecisionChange]
    );

    const onTolerancePick = useCallback(
        (index: number) => {
            if (index >= toleranceItems.length) {
                onToleranceChange("tight");
            } else {
                onToleranceChange(toleranceItems[index]);
            }
        },
        [onToleranceChange]
    );

    return (
        <SettingsWrapper orientation={orientation}>
            <div className={"close-button-container"}>
                <button className="close-button" onClick={onClose}>
                    <div>{X({ size: 11, color: theme.accentForegroundColor })}</div>
                </button>
            </div>
            <InlinePivotView
                items={precisionItems}
                title={getLocalizedString("precisionLabel", appKind)}
                current={precisionItems.indexOf(currentPrecision)}
                onClick={onPrecisionPick}
                formatItem={labelForPrecision as (p: string) => string}
                appKind={appKind}
            />
            <InlinePivotView
                items={toleranceItems}
                title={getLocalizedString("toleranceLabel", appKind)}
                current={toleranceItems.indexOf(currentTolerance)}
                onClick={onTolerancePick}
                formatItem={labelForTolerance as (t: string) => string}
                appKind={appKind}
            />
        </SettingsWrapper>
    );
}
